import { memo, MouseEventHandler, useCallback } from 'react';
import { ButtonProps } from 'antd';
import { useAdditionalFormContext } from '../../AdditionalFormContext';
import { useFocusTableRowField } from './useFocusTableRowField';
import { useInsertFormTableRow } from './useInsertFormTableRow';
import { CellContext } from '@tanstack/react-table';
import { useFormContext } from 'react-hook-form';
import { useFormTableName } from './TanstackFormTable';
import { CloneTableRowButton } from './CloneTableRowButton';

export interface CloneFormTableRowButtonProps extends Omit<ButtonProps, 'id'> {
  id?: string;
  context: CellContext<any, any>;
  resetId?: boolean;
  resetValues?: any;
  autoFocusField?: string;
}

export const CloneFormTableRowButton = memo(
  (props: CloneFormTableRowButtonProps) => {
    const { context, resetId, resetValues, autoFocusField, ...rest } = props;
    const { getValues } = useFormContext();
    const tableName = useFormTableName();
    const { onChange: formOnChange } = useAdditionalFormContext();

    const focusField = useFocusTableRowField(
      autoFocusField,
      context.row.index + 1
    );
    const insertRow = useInsertFormTableRow();
    const handleClick: MouseEventHandler<HTMLButtonElement> =
      useCallback(() => {
        const reset = resetId ? { id: null } : {};
        insertRow(context.row.index + 1, {
          ...getValues(`${tableName}.${context.row.index}`),
          ...reset,
          ...resetValues,
        });
        focusField();
        formOnChange?.();
      }, [
        insertRow,
        context,
        tableName,
        getValues,
        resetId,
        focusField,
        formOnChange,
      ]);

    return (
      <CloneTableRowButton context={context} onClick={handleClick} {...rest} />
    );
  }
);
